import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ReceiptPage from './pages/ReceiptPage';
import SessionReceiptVerify from './pages/SessionReceiptVerify';
import SessionConfirmPage from './pages/SessionConfirmPage';
import { Layout } from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ProviderGate from './components/ProviderGate';
import PostAuthRedirect from './components/PostAuthRedirect';
import NotificationPrompt from './components/NotificationPrompt';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { AccountType, ProviderRole } from './types';
import ScrollToTop from './components/ScrollToTop';
import { ComplianceWrapper } from './components/ComplianceWrapper';
const AiChatbot = lazy(() => import('./components/AiChatbot'));
const FeedbackButton = lazy(() => import('./components/FeedbackButton'));
const PhysioLocationPage = lazy(() => import('./pages/PhysioLocationPage'));
const ClaimAccount = lazy(() => import('./pages/ClaimAccount'));
const TeamsLanding = lazy(() => import('./pages/TeamsLanding'));
const OpenJobs = lazy(() => import('./pages/OpenJobs'));
const AdminEngagementPayouts = lazy(() => import('./pages/AdminEngagementPayouts'));
const PaymentsDashboard = lazyWithRetry('PaymentsDashboard', () => import('./pages/PaymentsDashboard'));
const PhysioLocationHubPage = lazy(() => import('./pages/PhysioLocationPage').then(m => ({ default: m.PhysioLocationHubPage })));
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { onForegroundMessage, refreshFcmToken } from './firebase';
import { sendTokenToBackend } from './api/notifications';
import { PageErrorBoundary } from './components/PageErrorBoundary';

//  Lazy loader with auto-retry on chunk failures 
const CHUNK_RELOAD_KEY = 'homphy_chunk_reload_attempted';

function isChunkLoadError(error: Error) {
  return (
    error.message.includes('Loading chunk') ||
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module') ||
    error.name === 'ChunkLoadError'
  );
}

function importWithTimeout<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  label: string,
  timeoutMs = import.meta.env.DEV ? 60000 : 15000
) {
  return new Promise<{ default: T }>((resolve, reject) => {
    let settled = false;

    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      const error = new Error(`Timed out loading ${label}`);
      error.name = 'LazyImportTimeout';
      reject(error);
    }, timeoutMs);

    factory().then(
      (module) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        resolve(module);
      },
      (error: Error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        reject(error);
      }
    );
  });
}

function lazyWithRetry<T extends React.ComponentType<any>>(
  label: string,
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(() =>
    importWithTimeout(factory, label).catch((err: Error) => {
      if (isChunkLoadError(err) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    })
  );
}

//  Lazy pages 
// NOTE: FindPhysio (/find) and MarketplaceMaps (/maps) have been removed.
//       The interactive physio map now lives in the Home page (#find section).
const Home              = lazyWithRetry('Home',              () => import('./pages/Home'));
//  NEW: Learn. Teach. Shop. 
// Old "How Homphy Works" explainer previously lived at /learn  its content now
// lives inline on Home. /learn is now the public Learner-vs-Educator landing page.
const LearnLanding       = lazyWithRetry('LearnLanding',       () => import('./pages/LearnLanding'));
const LearnerDashboard   = lazyWithRetry('LearnerDashboard',   () => import('./pages/LearnerDashboard'));
const Teach              = lazyWithRetry('Teach',              () => import('./pages/Teach'));
const CourseEditor       = lazyWithRetry('CourseEditor',       () => import('./pages/CourseEditor'));
const Shop                = lazyWithRetry('Shop',                () => import('./pages/Shop'));
const ShopProductPage     = lazyWithRetry('ShopProductPage',     () => import('./pages/ShopProductPage'));
const ShopOrders          = lazyWithRetry('ShopOrders',          () => import('./pages/ShopOrders'));
const CourseCatalog      = lazyWithRetry('CourseCatalog',      () => import('./pages/CourseCatalog'));
const CourseView         = lazyWithRetry('CourseView',         () => import('./pages/CourseView'));
const LearnPlayer        = lazyWithRetry('LearnPlayer',        () => import('./pages/LearnPlayer'));
const VerifyCertificate = lazyWithRetry('VerifyCertificate', () => import('./pages/VerifyCertificate'));
// 
const ProfileView       = lazyWithRetry('ProfileView',       () => import('./pages/ProfileView'));
const AuthPage          = lazyWithRetry('AuthPage',          () => import('./pages/AuthPage'));
const ResetPasswordPage = lazyWithRetry('ResetPasswordPage', () => import('./pages/ResetPasswordpage'));
const ClientDashboard   = lazyWithRetry('ClientDashboard',   () => import('./pages/ClientDashboard'));
const PhysioDashboard   = lazyWithRetry('PhysioDashboard',   () => import('./pages/PhysioDashboard'));
const WorkforceIntake   = lazyWithRetry('WorkforceIntake',   () => import('./pages/WorkforceIntake'));
const ProviderSubscriptionPage = lazyWithRetry('ProviderSubscriptionPage', () => import('./pages/ProviderSubscriptionPage'));
const AdminDashboard    = lazyWithRetry('AdminDashboard',    () => import('./pages/AdminDashboard'));
const WorkforceDashboard = lazyWithRetry('WorkforceDashboard', () => import('./pages/WorkforceDashboard'));
const AccessImpactDashboard = lazyWithRetry('AccessImpactDashboard', () => import('./pages/dashboards/AccessImpactDashboard'));
const PublicImpactPage = lazyWithRetry('PublicImpactPage', () => import('./pages/PublicImpactPage'));
//  Unified provider dashboard (replaces separate Doctor/Clinic dashboards) 
// All 9 provider cadres (incl. REFERRAL_GP and CLINIC) now route through this
// single dashboard, which internally renders the cadre-specific view.
const ProviderDashboard = lazyWithRetry('ProviderDashboard', () => import('./pages/ProviderDashboard'));
// 
const HelpCenter        = lazyWithRetry('HelpCenter',        () => import('./pages/HelpCenter'));
const ProfessionalRules = lazyWithRetry('ProfessionalRules', () => import('./pages/ProfessionalRules'));
const PatientsRights    = lazyWithRetry('PatientsRights',    () => import('./pages/PatientsRights'));
const TermsOfService    = lazyWithRetry('TermsOfService',    () => import('./pages/TermsOfService'));
const PrivacyPolicy     = lazyWithRetry('PrivacyPolicy',     () => import('./pages/PrivacyPolicy'));
const CrossBorderPolicy = lazyWithRetry('CrossBorderPolicy', () => import('./pages/CrossBorderPolicy'));
const HowWeVerify       = lazyWithRetry('HowWeVerify',       () => import('./pages/HowWeVerify'));
const MarketplaceViews  = lazyWithRetry('MarketplaceViews',  () => import('./pages/MarketplaceViews'));
const ReviewsSystem     = lazyWithRetry('ReviewsSystem',     () => import('./pages/ReviewsSystem'));
const ArticleList        = lazyWithRetry('ArticleList',        () => import('./pages/ArticleList'));
const ArticleDetail      = lazyWithRetry('ArticleDetail',      () => import('./pages/ArticleDetail'));
const ArticleComposer    = lazyWithRetry('ArticleComposer',    () => import('./pages/ArticleComposer'));

//  Page loader (Suspense fallback) 
const PageLoader = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-2 border-brand-100 border-t-brand-600 rounded-full animate-spin" />
      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Loading</p>
    </div>
  </div>
);

//  App content 
const AppContent: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      const title = payload.notification?.title || 'Homphy';
      const body = payload.notification?.body || '';
      const link = payload.data?.link;
      toast(title, {
        description: body,
        action: link ? { label: 'Open', onClick: () => navigate(link) } : undefined,
      });
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const saveToken = async () => {
      const token = await refreshFcmToken();
      if (token && token !== localStorage.getItem('homphy_fcm_token')) {
        await sendTokenToBackend(token);
        localStorage.setItem('homphy_fcm_token', token);
      }
    };
    void saveToken();
    window.addEventListener('visibilitychange', saveToken);
    return () => window.removeEventListener('visibilitychange', saveToken);
  }, [user]);

  const chatRole =
    user?.roles.includes(ProviderRole.PHYSIOTHERAPY) ? 'physio' :
    user?.roles.includes(ProviderRole.REFERRAL_GP) ? 'doctor' :
    user?.roles.includes(ProviderRole.CLINIC) ? 'clinic' :
    'client';

  const chatUserName = user?.name || undefined;

  return (
    <>
      <Layout>
        <PageErrorBoundary key={location.pathname}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/verify/:code" element={<SessionReceiptVerify />} />
              <Route path="/verify" element={<SessionReceiptVerify />} />
              <Route path="/session-confirm/:token" element={<SessionConfirmPage />} />
              {/* Public Routes */}
              <Route path="/"                    element={<Home />} />
              <Route path="/learn"               element={<LearnLanding />} />
              <Route path="/shop"                element={<Shop />} />
              <Route path="/shop/product/:id"    element={<ShopProductPage />} />
              <Route path="/shop/orders"          element={<ProtectedRoute><ShopOrders /></ProtectedRoute>} />
              <Route path="/physio/:id"          element={<ProfileView />} />
              <Route path="/physio-location" element={<PhysioLocationHubPage />} />
              <Route path="/physio-location/:city" element={<PhysioLocationPage />} />
              <Route path="/login"               element={<AuthPage mode="login" />} />
              <Route path="/register"            element={<AuthPage mode="register" />} />
              <Route path="/claim-account" element={<ClaimAccount />} />
        <Route path="/reset-password"      element={<ResetPasswordPage />} />
              <Route path="/help"                element={<HelpCenter />} />
              <Route path="/professional-rules"  element={<ProfessionalRules />} />
              <Route path="/patients-rights"     element={<PatientsRights />} />
              <Route path="/terms-of-service"    element={<TermsOfService />} />
              <Route path="/privacy-policy"      element={<PrivacyPolicy />} />
              <Route path="/cross-border-policy" element={<CrossBorderPolicy />} />
              <Route path="/how-we-verify"       element={<HowWeVerify />} />
              <Route path="/marketplace"         element={<MarketplaceViews />} />
              <Route path="/reviews"             element={<ReviewsSystem />} />
              <Route path="/articles"            element={<ArticleList />} />
              <Route
                path="/articles/new"
                element={
                  <ProtectedRoute allowedRoles={[AccountType.ADMIN]}>
                    <ArticleComposer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/articles/:id/edit"
                element={
                  <ProtectedRoute allowedRoles={[AccountType.ADMIN]}>
                    <ArticleComposer />
                  </ProtectedRoute>
                }
              />
              <Route path="/articles/:slug"      element={<ArticleDetail />} />
              <Route path="/impact" element={<PublicImpactPage />} />

              <Route path="/teams" element={<TeamsLanding />} />
      <Route path="/open-jobs" element={<ProtectedRoute allowedRoles={[ProviderRole.PHYSIOTHERAPY]}><OpenJobs /></ProtectedRoute>} />
      <Route path="/admin/engagement-payouts" element={<ProtectedRoute allowedRoles={[AccountType.ADMIN]}><AdminEngagementPayouts /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><PaymentsDashboard /></ProtectedRoute>} />

      {/* Protected Routes */}
              <Route path="/receipts/:id" element={<ProtectedRoute><ReceiptPage /></ProtectedRoute>} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={[AccountType.CLIENT]}>
                    <ClientDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/physio-dashboard"
                element={
                  <ProtectedRoute allowedRoles={[ProviderRole.PHYSIOTHERAPY]}>
                    <ProviderGate>
                      <PhysioDashboard />
                    </ProviderGate>
                  </ProtectedRoute>
                }
              />
              <Route path="/workforce-intake" element={<ProtectedRoute allowedRoles={Object.values(ProviderRole)}><WorkforceIntake /></ProtectedRoute>} />
              <Route path="/provider-subscription" element={<ProtectedRoute allowedRoles={Object.values(ProviderRole)}><ProviderSubscriptionPage /></ProtectedRoute>} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={[AccountType.ADMIN]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/workforce"
                element={
                  <ProtectedRoute allowedRoles={[AccountType.ADMIN]}>
                    <WorkforceDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/access-impact"
                element={
                  <ProtectedRoute allowedRoles={[AccountType.ADMIN]}>
                    <AccessImpactDashboard />
                  </ProtectedRoute>
                }
              />
              {/*  Unified provider dashboard 
                   /doctor-dashboard and /clinic-dashboard both redirect to the
                   single /provider-dashboard route, which renders the correct
                   cadre-specific view internally based on the user's role.  */}
              <Route
                path="/provider-dashboard"
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      ProviderRole.REFERRAL_GP,
                      ProviderRole.CLINIC,
                      ProviderRole.PHYSIOTHERAPY,
                      ProviderRole.EDUCATOR,
                      ProviderRole.OCCUPATIONAL_THERAPY,
                      ProviderRole.SPEECH_LANGUAGE_THERAPY,
                      ProviderRole.ORTHOTICS_PROSTHETICS,
                      ProviderRole.REHAB_PSYCHOLOGY,
                      ProviderRole.PHYSIATRY,
                      ProviderRole.REHAB_NURSING,
                      ProviderRole.REFERRAL_CHW,
                    ]}
                  >
                    <ProviderGate>
                      <ProviderDashboard />
                    </ProviderGate>
                  </ProtectedRoute>
                }
              />
              <Route path="/doctor-dashboard" element={<Navigate to="/provider-dashboard" replace />} />
              <Route path="/clinic-dashboard" element={<Navigate to="/provider-dashboard" replace />} />
              {/*  NEW: Educator ("Teach & Earn")  */}
              <Route
                path="/teach"
                element={
                  <ProtectedRoute allowedRoles={[ProviderRole.EDUCATOR]}>
                    <Teach />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teach/courses/:id"
                element={
                  <ProtectedRoute allowedRoles={[ProviderRole.EDUCATOR]}>
                    <CourseEditor />
                  </ProtectedRoute>
                }
              />
              {/*  NEW: Learner course dashboard  */}
              <Route
                path="/learn/dashboard"
                element={
                  <ProtectedRoute requireLearner>
                    <LearnerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/learn/catalog" element={<CourseCatalog />} />
              <Route path="/learn/course/:id" element={<CourseView />} />
              <Route path="/learn/course/:id/player" element={<LearnPlayer />} />
<Route path="/verify/:code" element={<VerifyCertificate />} />
              {/*  */}

              {/* 404  redirect home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </PageErrorBoundary>
      </Layout>

      {/* Global Overlays */}
      <Suspense fallback={null}>
        <AiChatbot
          role={chatRole}
          userName={chatUserName}
          extraContext={user ? `Logged in as ${user.role}` : 'Guest browsing Homphy'}
        />
      </Suspense>
      <Suspense fallback={null}>
        <FeedbackButton />
      </Suspense>
      <Toaster position="top-right" richColors />
    </>
  );
};

//  Root 
const App: React.FC = () => {
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#/')) {
      window.location.replace(hash.slice(1) + window.location.search);
    }
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <PostAuthRedirect />
          <NotificationPrompt />
          <ComplianceWrapper>
            <AppContent />
          </ComplianceWrapper>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;
